import { BellOutlined, CheckOutlined, ReloadOutlined } from "@ant-design/icons";
import { Badge, Button, Dropdown, Empty, Spin, Tag, Tooltip, Typography } from "antd";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { AuthContext } from "../../context/AuthContext";
import { Severty, ShowToast } from "../../helper/toast";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import moment from "moment";

const { Text } = Typography;

const TYPE_COLORS = {
  booking: "blue",
  system: "purple",
  alert: "red",
  promo: "green",
  general: "default",
};

const getRecipientType = (authRole) => {
  if (authRole === "hotel") return "hotel";
  if (authRole === "agent") return "agent";
  return "admin";
};

const getRecipientId = (userProfile) => {
  return (
    userProfile?._id ||
    userProfile?.id ||
    null
  );
};

function NotificationPanel() {
  const { userProfile } = useContext(AuthContext);
  const { request } = useRequest();
  const authRole = localStorage.getItem("authRole") || "admin";

  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);

  const recipientType = getRecipientType(authRole);
  const recipientId = getRecipientId(userProfile);

  // Fetch unread count (lightweight call)
  const fetchUnreadCount = useCallback(() => {
    if (!recipientId) return;
    request({
      url: `${apiPath.notifications}?recipientId=${recipientId}&recipientType=${recipientType}&isRead=false&limit=1`,
      method: "GET",
      onSuccess: (data) => {
        if (data?.success) {
          setUnreadCount(data.unreadCount ?? 0);
        }
      },
      onError: () => {},
    });
  }, [recipientId, recipientType, request]);

  // Fetch notifications page
  const fetchNotifications = useCallback(
    (pageNum = 1, append = false) => {
      if (!recipientId) return;
      if (pageNum === 1) setLoading(true);
      else setLoadingMore(true);

      request({
        url: `${apiPath.notifications}?recipientId=${recipientId}&recipientType=${recipientType}&page=${pageNum}&limit=10&sort=desc`,
        method: "GET",
        onSuccess: (data) => {
          if (data?.success) {
            const incoming = data.notifications || [];
            setNotifications((prev) => (append ? [...prev, ...incoming] : incoming));
            setUnreadCount(data.unreadCount ?? 0);
            setPage(pageNum);
            setTotalPages(data.totalPages ?? 1);
          }
          setLoading(false);
          setLoadingMore(false);
        },
        onError: () => {
          setLoading(false);
          setLoadingMore(false);
        },
      });
    },
    [recipientId, recipientType, request]
  );

  // Load unread badge count on mount & whenever recipientId changes
  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  // When panel opens, refresh full list
  const handleOpenChange = (nextOpen) => {
    setOpen(nextOpen);
    if (nextOpen) {
      fetchNotifications(1, false);
    }
  };

  const handleMarkRead = (notif) => {
    if (notif.isRead) return;
    request({
      url: apiPath.markNotificationRead(notif._id),
      method: "PUT",
      onSuccess: (data) => {
        if (data?.success) {
          setNotifications((prev) =>
            prev.map((n) => (n._id === notif._id ? { ...n, isRead: true } : n))
          );
          setUnreadCount((c) => Math.max(0, c - 1));
        }
      },
      onError: () => {},
    });
  };

  const handleMarkAllRead = () => {
    if (!recipientId) return;
    setMarkingAll(true);
    request({
      url: apiPath.markAllNotificationsRead,
      method: "PUT",
      data: { recipientId, recipientType },
      onSuccess: (data) => {
        if (data?.success) {
          setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
          setUnreadCount(0);
          ShowToast("All notifications marked as read", Severty.SUCCESS);
        }
        setMarkingAll(false);
      },
      onError: () => {
        setMarkingAll(false);
      },
    });
  };

  const handleLoadMore = () => {
    if (page < totalPages) {
      fetchNotifications(page + 1, true);
    }
  };

  const dropdownContent = (
    <div
      style={{
        width: 380,
        maxHeight: 520,
        display: "flex",
        flexDirection: "column",
        background: "#fff",
        borderRadius: 10,
        boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 16px 10px",
          borderBottom: "1px solid #f0f0f0",
          flexShrink: 0,
        }}
      >
        <span style={{ fontWeight: 700, fontSize: 15 }}>
          Notifications{" "}
          {unreadCount > 0 && (
            <span
              style={{
                background: "#ff4d4f",
                color: "#fff",
                borderRadius: 999,
                padding: "1px 8px",
                fontSize: 11,
                marginLeft: 4,
              }}
            >
              {unreadCount}
            </span>
          )}
        </span>
        <div style={{ display: "flex", gap: 8 }}>
          <Tooltip title="Refresh">
            <Button
              type="text"
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => fetchNotifications(1, false)}
              disabled={loading}
            />
          </Tooltip>
          {unreadCount > 0 && (
            <Tooltip title="Mark all as read">
              <Button
                type="text"
                size="small"
                icon={<CheckOutlined />}
                loading={markingAll}
                onClick={handleMarkAllRead}
              >
                Mark all read
              </Button>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Body */}
      <div style={{ overflowY: "auto", flex: 1 }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Spin />
          </div>
        ) : notifications.filter((n) => n.status !== "inactive").length === 0 ? (
          <Empty
            description="No notifications"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ padding: "32px 0" }}
          />
        ) : (
          notifications
            .filter((n) => n.status !== "inactive")
            .map((notif) => (
              <div
                key={notif._id}
                onClick={() => handleMarkRead(notif)}
                style={{
                  padding: "12px 16px",
                  borderBottom: "1px solid #f5f5f5",
                  cursor: notif.isRead ? "default" : "pointer",
                  background: notif.isRead ? "#fff" : "#f0f5ff",
                  transition: "background 0.2s",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 8,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: notif.isRead ? 400 : 700,
                        fontSize: 13,
                        color: "#1a1a2e",
                        marginBottom: 2,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {notif.title}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: "#595959",
                        lineHeight: 1.5,
                        marginBottom: 4,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {notif.message}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <Tag
                        color={TYPE_COLORS[notif.type] || "default"}
                        style={{ fontSize: 10, padding: "0 5px", marginRight: 0 }}
                      >
                        {notif.type || "general"}
                      </Tag>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {moment(notif.createdAt).fromNow()}
                      </Text>
                    </div>
                  </div>
                  {!notif.isRead && (
                    <div
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: "#1890ff",
                        flexShrink: 0,
                        marginTop: 4,
                      }}
                    />
                  )}
                </div>
              </div>
            ))
        )}

        {/* Load more */}
        {page < totalPages && (
          <div style={{ textAlign: "center", padding: "10px 0" }}>
            <Button
              size="small"
              loading={loadingMore}
              onClick={handleLoadMore}
            >
              Load more
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Dropdown
      open={open}
      onOpenChange={handleOpenChange}
      dropdownRender={() => dropdownContent}
      trigger={["click"]}
      placement="bottomRight"
    >
      <Button
        type="text"
        style={{ padding: "4px 8px", height: "auto" }}
      >
        <Badge
          count={unreadCount}
          overflowCount={99}
          size="small"
          offset={[-2, 2]}
        >
          <BellOutlined style={{ fontSize: 20, color: "#595959" }} />
        </Badge>
      </Button>
    </Dropdown>
  );
}

export default NotificationPanel;
